import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { createSelector } from 'reselect';

import { prokeyApi } from 'api/prokey';

//#region [red] Constants

export const constants = {
  ACCEPT: 'FIELDS_ACCEPT',
  CLEAR: 'FIELDS_CLEAR',
  CONTENT: 'FIELDS_CONTENT',
  DISPLAY_ALL: 'FIELDS_DISPLAY_ALL',
  DISPLAY_EXPCEPTIONS: 'FIELDS_DISPLAY_EXPCEPTIONS',
  DISPLAY_UNCERTAIN: 'FIELDS_DISPLAY_UNCERTAIN',
  DISPLAY_CROSS_DOC_GROUP: 'FIELDS_DISPLAY_CROSS_DOC_GROUP',
  FOCUS_NEXT_UNACCEPTED: 'FIELDS_FOCUS_NEXT_UNACCEPTED',
  FOCUS_NEXT: 'FIELDS_FOCUS_NEXT',
  SET: 'FIELDS_SET',
  ADD: 'FIELDS_ADD',
  REMOVE: 'FIELDS_REMOVE',
  UPDATE_FIELD: 'FIELDS_UPDATE_FIELD',
  ADD_FOCUS: 'FIELDS_ADD_FOCUS',
  REJECT: 'FIELDS_REJECT',
  REMOVE_FOCUS: 'FIELDS_REMOVE_FOCUS',
  TOGGLE: 'FIELDS_TOGGLE',
  UPDATE_REQUEST: 'FIELDS_UPDATE_REQUEST',
  UPDATE_SUCCESS: 'FIELDS_UPDATE_SUCCESS',
  UPDATE_FAILURE: 'FIELDS_UPDATE_FAILURE',
};

//#endregion

//#region [purple] Actions

export const actions = {
  accept,
  clear,
  content,
  displayAll,
  displayExceptions,
  displayUncertain,
  displayCrossDocGroup,
  focusNextUnaccepted,
  focusNext,
  addFocus,
  reject,
  removeFocus,
  set,
  add,
  remove,
  toggle,
  update,
  updateField,
};

function accept(id) {
  return { type: constants.ACCEPT, id };
}

function clear() {
  return { type: constants.CLEAR };
}

function set(fields) {
  return { type: constants.SET, fields };
}

function add(fields, start_index) {
  return { type: constants.ADD, fields, start_index };
}

function remove(fields, start_index, count) {
  return { type: constants.REMOVE, fields, start_index, count };
}

function updateField(field) {
  return { type: constants.UPDATE_FIELD, field };
}

function displayExceptions() {
  return { type: constants.DISPLAY_EXPCEPTIONS };
}

function displayAll() {
  return { type: constants.DISPLAY_ALL };
}

function displayUncertain() {
  return { type: constants.DISPLAY_UNCERTAIN }
}

function displayCrossDocGroup(group) {
  return { type: constants.DISPLAY_CROSS_DOC_GROUP, group };
}

function focusNextUnaccepted() {
  return { type: constants.FOCUS_NEXT_UNACCEPTED };
}

function focusNext() {
  return { type: constants.FOCUS_NEXT };
}

function addFocus(id) {
  return { type: constants.ADD_FOCUS, id };
}

function reject(id) {
  return { type: constants.REJECT, id };
}

function removeFocus(id) {
  return { type: constants.REMOVE_FOCUS, id };
}

function toggle(id) {  
  return { type: constants.TOGGLE, id };
}

function content(id, content) {
  return { type: constants.CONTENT, id, content };
}

function update(field, focusType = 'unaccepted') {
  return async (dispatch) => {
    try {
      prokeyApi.updateField(field);
      dispatch(accept(field.id));

      if (focusType === 'next') dispatch(focusNext());
      else dispatch(focusNextUnaccepted());
    } catch (error) {
      console.log(error);
    }
  };
}

//#endregion

//#region [blue] Reducers

const initialState = {
  mode: constants.DISPLAY_ALL,
  isFiltered: false,
  focusedField: -1,
  items: {},
  order: [],
};

export default function reducer(state = initialState, action) {
  switch (action.type) {
    case constants.ACCEPT: {
      let newState = { ...state };
      newState.items[action.id].validationInfo.isAccepted = true;
      newState.items[action.id].validationInfo.isRejected = false;
      return newState;
    }

    case constants.CLEAR:
      return initialState;

    case constants.CONTENT: {
      let newState = { ...state };
      newState.items[action.id].Entries[0].Content = action.content;
      return newState;
    }

    case constants.DISPLAY_CROSS_DOC_GROUP: {
      return _displayCrossDocGroup(state, action);
    }

    case constants.DISPLAY_ALL: {
      return _displayAll(state, action);
    }

    case constants.DISPLAY_EXPCEPTIONS: {
      return _displayExceptions(state, action);
    }

    case constants.DISPLAY_UNCERTAIN: {
      return _displayUncertain(state, action);
    }

    case constants.FOCUS_NEXT_UNACCEPTED: {
      let current_focus = -1;
      let available_before = -1;
      let available_after = -1;

      let newState = { ...state };

      let index = -1;
      for (let id of newState.order) {
        let field = newState.items[id];
        index++;

        if(field.isReadOnly) continue;

        if (field.isVisible && !field.validationInfo.isAccepted) {
          // is there an available field before the current one?
          if (available_before < 0 && current_focus === -1) {
            available_before = index;
          }

          // is there an available field after the current one?
          if (available_after < 0 && current_focus > -1) {
            available_after = index;
          }
        }

        if (field.Id === state.focusedField) {
          current_focus = index;
        }
      }

      // focus the next available field; prioritize fields after the current
      const nextField = available_after >= 0 ? available_after : available_before;
      if (nextField >= 0) {
        let field_id = newState.order[nextField];
        newState.focusedField = field_id;
      }

      return newState;
    }

    case constants.FOCUS_NEXT: {
      let current_focus = -1;
      let available_before = -1;
      let available_after = -1;

      let newState = { ...state };

      let index = -1;
      for (let id of newState.order) {
        let field = newState.items[id];
        index++;

        if(field.isReadOnly) continue;
        
        if (field.isVisible) {
          // is there an available field before the current one?
          if (available_before < 0 && current_focus === -1) {
            available_before = index;
          }

          // is there an available field after the current one?
          if (available_after < 0 && current_focus > -1) {
            available_after = index;
          }
        }

        if (field.Id === state.focusedField) {
          current_focus = index;
        }
      }

      // focus the next visible field; prioritize fields after the current
      const nextField = available_after >= 0 ? available_after : available_before;
      if (nextField >= 0) {
        let field_id = newState.order[nextField];
        newState.focusedField = field_id;
      }

      return newState;
    }

    case constants.ADD_FOCUS: {
      if(action.id === -1) return state;
      
      let newState = { ...state };
      const length = newState.order.length;
      // There can be only one (field with focus)
      for( let index = 0; index < length; index++ ) {
        const id = newState.order[index];
        newState.items[id].focus = false;
      }
      newState.focusedField = action.id;
      newState.items[action.id].focus = true;
      return newState;
    }

    case constants.REMOVE_FOCUS: {
      if (!state.items[action.id].focus) return state;

      let newState = { ...state };
      newState.focusedField = -1;
      newState.items[action.id].focus = false;
      return newState;
    }

    case constants.REJECT: {
      let newState = { ...state };
      newState.items[action.id].validationInfo.isAccepted = false;
      newState.items[action.id].validationInfo.isRejected = true;
      return newState;
    }

    case constants.SET: {
      return {...state, ...action.fields, focusedField: -1};
    }

    case constants.TOGGLE: {
      let newState = { ...state };

      if( action.id === -1 ) {        
        const length = newState.order.length;
        for( let index = 0; index < length; index++ ) {
          const id = newState.order[index];
          if ( newState.focusedField === id ) {
            newState.items[id].toggle = !newState.items[id].toggle;
          }
        }
      } else {        
        newState.items[action.id].toggle = !newState.items[action.id].toggle;
      }
      return newState;
    }

    case constants.ADD: {
      let newState = {...state};
      action.fields.forEach(field=>{
        newState.items[field.Id] = field;
      });
      newState.order.splice(action.start_index,0, ...action.fields.map(x=>x.Id));
      return newState;
    }

    case constants.REMOVE: {
      let newState = {...state};
      action.fields.forEach(id=>{
        delete newState.items[id];
      });
      newState.order.splice(action.start_index,action.count);
      return newState;
    }

    case constants.UPDATE_FIELD: {
      let newState = {...state};
      action.field.focus = newState.items[action.field.Id].focus;
      action.field.isVisible = newState.items[action.field.Id].isVisible;
      newState.items[action.field.Id] = action.field;
      return newState;
    }

    case constants.UPDATE_REQUEST: {
      let newState = [...state];
      newState[action.field.index].updating = true;
      return newState;
    }

    case constants.UPDATE_SUCCESS: {
      let newState = [...state];
      // make a copy of the field without the 'updating' property
      let { updating, ...fieldCopy } = newState[action.field.index];
      fieldCopy.Entries[0].Content = action.field.content;
      fieldCopy.Entries[0].State = action.field.state;
      fieldCopy.Entries[0].ValidationTime = action.field.validation_time;
      newState[action.field.index] = fieldCopy;
      return newState;
    }

    case constants.UPDATE_FAILURE: {
      let newState = [...state];
      // make a copy of the field without the 'updating' property
      let { updating, ...fieldCopy } = newState[action.field.index];
      fieldCopy.error = action.field.error;
      newState[action.field.index] = fieldCopy;
      return newState;
    }

    default:
      return state;
  }
}

function _displayAll(state, action) {
  let newState = { ...state };
  newState.mode = constants.DISPLAY_ALL;
  newState.isFiltered = false;
  for (let id of newState.order) {
    let field = newState.items[id];
    field.isVisible = true;
  }

  let index_of_field_with_focus = Object.values(newState.items).findIndex(x=>x.focus);
  let no_focused_field = index_of_field_with_focus === -1;

  if(no_focused_field) {
    let first_unaccepted_field_id = newState.order.find( x => {
      const result = !newState.items[x].validationInfo.isAccepted && !newState.items[x].isReadOnly;
      return result;
    });
    let first_unaccepted_field = newState.items[first_unaccepted_field_id];
    if(first_unaccepted_field) {
      newState.focusedField = first_unaccepted_field_id;
    } else {
      let first_field_id = newState.order[0];
      let first_field = newState.items[first_field_id];
      if(first_field) {
        newState.focusedField = first_field_id;
      }
    }
  }

  return newState;
}

function _displayExceptions(state, action) {
  let newState = { ...state };

  newState.mode = constants.DISPLAY_EXPCEPTIONS;
  newState.isFiltered = false;
  let is_focused_field_visible = false;

  for (let id of newState.order) {
    let field = newState.items[id];
    field.isVisible = field.validationInfo.isRequired;
    if(id === state.focusedField) {
      if(field.isVisible) is_focused_field_visible = true;
    }
    if(field.focus){
      if(field.isVisible) is_focused_field_visible = true;
      else field.focus = false;
    }
  }

  // If the previously focused field will no longer be visible
  //  look for the first unaccepted field and give it focus
  if(!is_focused_field_visible) {
    for (let index = 0; index < newState.order.length; index++) {
      let field = newState.items[newState.order[index]];
      if (field.validationInfo.isRequired && !field.validationInfo.isAccepted) {
        newState.focusedField = field.Id;
        break;
      }
    }  
  }

  let index_of_field_with_focus = Object.values(newState.items).findIndex(x=>x.focus);
  let no_focused_field = index_of_field_with_focus === -1;

  if(no_focused_field) {
    let first_unaccepted_field_id = newState.order.find( x => newState.items[x].validationInfo.isRequired && !newState.items[x].validationInfo.isAccepted && !newState.items[x].isReadOnly);
    let first_unaccepted_field = newState.items[first_unaccepted_field_id];
    if(first_unaccepted_field) {
      newState.focusedField = first_unaccepted_field_id;
    } else {
      let first_field_id = newState.order.find( x => newState.items[x].validationInfo.isRequired);
      let first_field = newState.items[first_field_id];
      if(first_field) {
        newState.focusedField = first_field_id;
      }
    }
  }
  
  return newState;
}

function _displayUncertain(state, action) {
  let newState = { ...state };
  newState.mode = constants.DISPLAY_UNCERTAIN;
  newState.isFiltered = false;
  for (let id of newState.order) {
    let field = newState.items[id];
    field.isVisible = true;
  }

  let index_of_field_with_focus = Object.values(newState.items).findIndex(x=>x.focus);
  let no_focused_field = index_of_field_with_focus === -1;

  if(no_focused_field) {
    let first_unaccepted_field_id = newState.order.find( x => {
      const result = !newState.items[x].validationInfo.isAccepted && !newState.items[x].isReadOnly;
      return result;
    });
    let first_unaccepted_field = newState.items[first_unaccepted_field_id];
    if(first_unaccepted_field) {
      newState.focusedField = first_unaccepted_field_id;
    } else {
      let first_field_id = newState.order[0];
      let first_field = newState.items[first_field_id];
      if(first_field) {
        newState.focusedField = first_field_id;
      }
    }
  }

  return newState;
}

function _displayCrossDocGroup(state, action) {
  if(state.isFiltered) {
    if(state.mode === constants.DISPLAY_ALL) return _displayAll(state, action);
    else if(state.mode === constants.DISPLAY_EXPCEPTIONS) return _displayExceptions(state, action);
    else if(state.mode === constants.DISPLAY_UNCERTAIN) return _displayUncertain(state, action);
  } else {
    let newState = { ...state };
    newState.isFiltered = true;
    for (let id of newState.order) {
      let field = newState.items[id];
      const is_not_in_group = field.validationInfo.CrossDocComparisonGroup !== action.group;
      if(field.isVisible && is_not_in_group) {
        field.isVisible = false;
      }
    }
    return newState;
  }

  return state;
}

//#endregion

//#region [purple] hooks

function intersect_for(a, b) {
  const result = [];
  const alen = a.length;
  const blen = b.length;
  for (let i = 0; i < alen; ++i) {
    const ai = a[i];
    for (let j = 0; j < blen; ++j) {
      if (ai === b[j]) {
        result.push(ai);
        break;
      }
    }
  }
  return result;
}

const selectAcceptableFields = (state) => {
  let result = [];
  for (let id of state.fields.order) {
    let field = state.fields.items[id];
    if (field.isVisible && !field.isReadOnly) result.push(field);
  }
  return result;
};

const selectVisibleFields = (state) => {
  let result = [];
  for (let id of state.fields.order) {
    let field = state.fields.items[id];
    if (field.isVisible) result.push(field);
  }
  return result;
};

const selectAcceptedFields = (state) => {
  let result = [];
  for (let id of state.fields.order) {
    let field = state.fields.items[id];
    if (field.validationInfo.isAccepted) result.push(field);
  }
  return result;
};

// Total number of acceptable fields
const selectNumOfAcceptableFields = () => {
  return createSelector(selectAcceptableFields, (fields) => fields.length);
};

export const useTotalAcceptableFields = () => {
  const selectNumOfFields = useMemo(selectNumOfAcceptableFields, []);

  const numOfVisibleFields = useSelector((state) => selectNumOfFields(state));

  return numOfVisibleFields;
};

// Total number of visible fields
const selectNumOfVisibleFields = () => {
  return createSelector(selectVisibleFields, (fields) => fields.length);
};

export const useTotalFields = () => {
  const selectNumOfFields = useMemo(selectNumOfVisibleFields, []);

  const numOfVisibleFields = useSelector((state) => selectNumOfFields(state));

  return numOfVisibleFields;
};

// Total number of visible and accepted fields
const selectNumOfAcceptedFields = () =>
  createSelector(
    [selectAcceptableFields, selectAcceptedFields],
    (visible, accepted) => intersect_for(visible, accepted).length
  );

export const useAcceptedFields = () => {
  const selectNumOfFields = useMemo(selectNumOfAcceptedFields, []);

  const numOfAcceptedFields = useSelector((state) => selectNumOfFields(state));

  return numOfAcceptedFields;
};

// Completion Percentage
export const useFieldCompletion = () => {
  const total = useTotalAcceptableFields();
  const accepted = useAcceptedFields();

  const result = total > 0 ? (accepted / total) * 100 : 0;

  return result;
};

export const useAllFieldsAccepted = () => {
  const completion = useFieldCompletion();
  return completion === 100;
};

export const hooks = {
  useAcceptedFields,
  useFieldCompletion,
  useTotalFields,
};

//#endregion
